export const EVENT_ASSET_MAX_BYTES = 25 * 1024 * 1024;

export const EVENT_ASSET_MEDIA_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/csv",
  "text/plain",
] as const;

export type EventAssetMediaType = (typeof EVENT_ASSET_MEDIA_TYPES)[number];

/**
 * What a file picker is told to offer, for the same files the server accepts.
 *
 * Extensions **and** media types, together, because a picker that cannot resolve one
 * falls back to the other. Every upload in the application used to name extensions
 * alone, and on 13 September a presenter reported that the picker "wanted to upload the
 * whole folder, not just one or two items in the folder" — which is what a picker shows
 * when its filter matches no file it can see: directories stay selectable and documents
 * do not.
 *
 * Built from `EVENT_ASSET_MEDIA_TYPES` rather than written beside it, so what a teacher
 * is allowed to choose and what the server will take cannot drift apart.
 */
export const EVENT_ASSET_EXTENSIONS = [
  ".pdf", ".docx", ".xlsx", ".pptx", ".png", ".jpg", ".jpeg", ".webp", ".csv", ".txt",
] as const;

export const EVENT_ASSET_ACCEPT = [...EVENT_ASSET_EXTENSIONS, ...EVENT_ASSET_MEDIA_TYPES].join(",");

export type EventAssetValidationErrorCode =
  | "file_empty"
  | "file_name_invalid"
  | "file_too_large"
  | "file_type_unsupported";

export class EventAssetValidationError extends Error {
  constructor(
    readonly code: EventAssetValidationErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "EventAssetValidationError";
  }
}

export interface EventAssetDescriptor {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
}

export function validateEventAsset(input: EventAssetDescriptor): EventAssetDescriptor & { mediaType: EventAssetMediaType } {
  const fileName = input.fileName.normalize("NFC").trim();
  if (fileName.length === 0 || fileName.length > 180 || /[\u0000-\u001f\u007f/\\]/u.test(fileName)) {
    throw new EventAssetValidationError("file_name_invalid", "Use a file name without folders or control characters.");
  }
  if (!Number.isSafeInteger(input.sizeBytes) || input.sizeBytes <= 0) {
    throw new EventAssetValidationError("file_empty", "Choose a file that contains data.");
  }
  if (input.sizeBytes > EVENT_ASSET_MAX_BYTES) {
    throw new EventAssetValidationError("file_too_large", "Event files must be 25 MB or smaller.");
  }
  if (!isEventAssetMediaType(input.mediaType)) {
    throw new EventAssetValidationError(
      "file_type_unsupported",
      "Use a PDF, Office document, image, CSV, or plain-text file.",
    );
  }
  return { fileName, mediaType: input.mediaType, sizeBytes: input.sizeBytes };
}

function isEventAssetMediaType(value: string): value is EventAssetMediaType {
  return (EVENT_ASSET_MEDIA_TYPES as readonly string[]).includes(value);
}
